import { MatchOperator } from "./MatchOperator";
import { useState, useEffect } from "react";
import { useAuthoringServices } from "./services";
import { hostSourceKind, type DamProviderInfo } from "@proof/core";
import {
  addDatasetVersion,
  newStatementDataset,
  statementOptions,
  datasetSchema,
  datasetVersion,
  type Field,
  type Project,
  type Dataset,
  uid,
  userLookupCatalog,
  orderLookupCatalog,
} from "@proof/core";
import { DatasetEditor } from "./DatasetEditor";
import { Select, Button, Control, JsonEditor, Toggle } from "./components";
import type { EditorProps } from "./Configuration";

export function DatasetManager({ project, onChange, notify }: EditorProps) {
  const services = useAuthoringServices();
  const [selected, select] = useState(""),
    [drafts, setDrafts] = useState<Record<string, Dataset>>({});
  const latest = project.datasets.filter(
    (d) =>
      !project.datasets.some(
        (other) => other.id === d.id && other.version > d.version,
      ),
  );
  const dataset = latest.find((d) => d.id === selected) || latest[0];
  const draftKey = dataset
    ? `${project.id}:${dataset.id}:${dataset.version}`
    : "";
  const draft = drafts[draftKey];
  const clearDraft = () =>
    setDrafts((current) => {
      const next = { ...current };
      delete next[draftKey];
      return next;
    });
  const create = (kind: "choices" | "table" | "statement") => {
    try {
      const id = uid("dataset");
      const next =
        kind === "statement"
          ? newStatementDataset(id)
          : datasetSchema.parse({
              id,
              name: kind === "table" ? "New table" : "New choices",
              kind,
              ...(kind === "table"
                ? {
                    columns: [
                      { key: "id", label: "ID" },
                      { key: "name", label: "Name" },
                    ],
                    primaryKey: "id",
                    labelColumn: "name",
                    rows: [{ id: "one", name: "First item" }],
                  }
                : { choices: [{ value: "one", label: "First item" }] }),
            });
      onChange(addDatasetVersion(project, next, 0));
      select(id);
    } catch (e) {
      notify((e as Error).message);
    }
  };
  return (
    <section className="dataset-manager" aria-label="Document datasets">
      <h3>Document datasets ({latest.length})</h3>
      <p className="muted">
        Publish a new version to change data. Existing fields keep their
        selected version. Table cells support text, numbers, booleans and null.
        Add independent lists and tables to the same document. Switching
        datasets keeps unfinished edits until you publish them or leave this
        editor.
      </p>
      <div className="button-row">
        <Button onClick={() => create("choices")}>Add choice dataset</Button>
        <Button onClick={() => create("table")}>Add table dataset</Button>
        {services.editorPresentation?.enableStatements !== false && (
          <Button onClick={() => create("statement")}>
            Add statement group
          </Button>
        )}
      </div>
      {!dataset && (
        <p className="muted">
          No datasets in this document yet. Add a choice list or table here, or
          apply a saved dataset from the reusable library.
        </p>
      )}
      {dataset && (
        <>
          <Control label="Edit dataset">
            <Select
              value={dataset.id}
              onChange={(e) => {
                select(e.target.value);
              }}
            >
              {latest.map((d) => (
                <option key={d.id} value={d.id}>
                  {d.name} · v{d.version} · {d.kind}
                </option>
              ))}
            </Select>
          </Control>
          {draft && <p role="status">Unpublished edits for {draft.name}.</p>}
          <p>
            {project.datasets.filter((d) => d.id === dataset.id).length}{" "}
            retained versions ·{" "}
            {dataset.kind === "table"
              ? dataset.rows.length
              : dataset.choices.length}{" "}
            items
          </p>
          <DatasetEditor
            key={draftKey}
            value={draft || dataset}
            onChange={(value) =>
              setDrafts((current) => ({ ...current, [draftKey]: value }))
            }
          />
          <Button
            onClick={() => {
              try {
                onChange(
                  addDatasetVersion(project, draft || dataset, dataset.version),
                );
                clearDraft();
                notify(`Published dataset version ${dataset.version + 1}`);
              } catch (e) {
                notify((e as Error).message);
              }
            }}
          >
            Publish dataset version
          </Button>
          <details>
            <summary>Advanced dataset JSON</summary>
            <JsonEditor
              key={draftKey}
              label="Dataset definition"
              value={dataset}
              onSave={(value) => {
                const definition = datasetSchema.parse(value);
                if (definition.id !== dataset.id)
                  throw new Error(
                    "Keep the dataset ID when publishing a version",
                  );
                onChange(
                  addDatasetVersion(project, definition, dataset.version),
                );
                clearDraft();
                notify(`Published dataset version ${dataset.version + 1}`);
              }}
            />
          </details>
        </>
      )}
    </section>
  );
}
export function DatasetSource({
  project,
  field,
  update,
  reset,
  notify,
}: {
  project: Project;
  field: Field;
  update: (patch: Partial<Field>) => void;
  reset?: () => void;
  notify: (message: string) => void;
}) {
  const source = field.source;
  const services = useAuthoringServices();
  const [providers, setProviders] = useState<DamProviderInfo[]>([]);
  useEffect(() => {
    let live = true;
    void services
      .listDamProviders?.()
      .then((value) => {
        if (live) setProviders(value);
      })
      .catch(() => undefined);
    return () => {
      live = false;
    };
  }, [services.listDamProviders]);
  const dataset =
    ["dataset", "lookup", "statement"].includes(source.kind) && source.datasetId
      ? datasetVersion(project, source)
      : undefined;
  const binding = (
    id: string,
    version: number,
    kind = source.kind,
  ): Field["source"] => {
    const data = project.datasets.find(
      (d) => d.id === id && d.version === version,
    )!;
    if (kind === "statement") {
      return {
        kind,
        datasetId: id,
        datasetVersion: version,
        ...(source.kind === "statement" &&
        (!source.datasetId || source.datasetId === id) &&
        source.key
          ? { key: source.key }
          : {}),
        ...(source.statementId &&
        data.rows.some(
          (row) => String(row[data.primaryKey!]) === source.statementId,
        )
          ? { statementId: source.statementId }
          : {}),
        labelColumn: data.labelColumn || data.primaryKey,
        resultColumn:
          data.columns.find(
            (column) =>
              column.key === source.resultColumn && column.type === "string",
          )?.key ||
          data.columns.find(
            (column) => column.key === "content" && column.type === "string",
          )?.key ||
          data.columns
            .filter(
              (column) =>
                column.type === "string" && column.key !== data.primaryKey,
            )
            .at(-1)?.key,
      };
    }
    if (kind === "lookup") {
      const selector =
        project.fields.find(
          (f) =>
            f.id !== field.id &&
            f.source.kind === "dataset" &&
            f.source.datasetId === id,
        ) ||
        project.fields.find(
          (f) => f.id !== field.id && f.source.kind !== "lookup",
        );
      return {
        kind: "lookup",
        datasetId: id,
        datasetVersion: version,
        lookupFieldId: project.fields.some(
          (f) => f.id === source.lookupFieldId && f.id !== field.id,
        )
          ? source.lookupFieldId
          : selector?.id,
        matchColumn:
          source.matchColumn &&
          data.columns.some((c) => c.key === source.matchColumn)
            ? source.matchColumn
            : data.primaryKey,
        resultColumn:
          source.resultColumn &&
          data.columns.some((c) => c.key === source.resultColumn)
            ? source.resultColumn
            : data.columns.find((c) => c.key !== data.primaryKey)?.key ||
              data.primaryKey,
      };
    }
    return { kind: "dataset", datasetId: id, datasetVersion: version };
  };
  const latest = project.datasets.filter(
    (d) =>
      !project.datasets.some(
        (other) => other.id === d.id && other.version > d.version,
      ),
  );
  const sourceInputs =
    services.editorPresentation?.enableSourceInputs === true;
  return (
    <>
      {sourceInputs && (
        <Toggle
          label="Use Source Input"
          value={!!source.hostInput}
          onChange={(hostInput) =>
            update({
              source: hostInput
                ? { ...source, hostInput: true }
                : { ...source, hostInput: undefined, hostInputKey: undefined },
            })
          }
        />
      )}
      {sourceInputs && source.hostInput && (
        <Control
          label="Source input key"
          note={`The host page receives this key with a ${
            hostSourceKind(field) === "asset" ? "asset" : "dataset"
          } request. Leave blank to use the variable token.`}
          help="When the form loads, the integration asks the parent page for this source through the proof:source-request event or sourceInputHandler. The host may return a translate function that maps its own data into the expected structure."
          helpDocs="/help?guide=embedding"
        >
          <input
            value={source.hostInputKey || ""}
            placeholder={field.key}
            onChange={(e) =>
              update({
                source: {
                  ...source,
                  hostInputKey: e.target.value.trim() || undefined,
                },
              })
            }
          />
        </Control>
      )}
      <Control label="Source" reset={reset}>
        <Select
          value={source.kind}
          onChange={(e) => {
            const kind = e.target.value as Field["source"]["kind"];
            if (
              kind === "dataset" ||
              kind === "lookup" ||
              kind === "statement"
            ) {
              const data = latest.find(
                (d) =>
                  !["lookup", "statement"].includes(kind) || d.kind === "table",
              );
              if (!data || (kind === "lookup" && project.fields.length < 2)) {
                notify(
                  kind === "statement"
                    ? "Add a statement group or a table with text content in Document datasets first"
                    : "Create a dataset and a selector variable in Rules & presets first",
                );
                return;
              }
              update({ source: binding(data.id, data.version, kind) });
            } else if (kind === "dam")
              update({
                source: {
                  kind,
                  providerId: providers[0]?.id || "default",
                  folderId: "root",
                },
              });
            else update({ source: { kind } });
          }}
        >
          {[
            "literal",
            "user",
            "order",
            "dataset",
            "lookup",
            "form",
            "asset",
            ...(["image", "image-list"].includes(field.type) ? ["dam"] : []),
            "image-rule",
            "dynamic",
            ...(["text", "textarea", "richtext"].includes(field.type) ||
            source.kind === "statement"
              ? ["statement"]
              : []),
          ]
            .filter(
              (kind) =>
                kind === source.kind ||
                ((kind !== "statement" ||
                  services.editorPresentation?.enableStatements !== false) &&
                  (kind !== "dynamic" ||
                    services.editorPresentation?.enableDynamicSources !==
                      false)),
            )
            .map((kind) => (
              <option key={kind}>{kind}</option>
            ))}
        </Select>
      </Control>
      {source.kind === "dynamic" && (
        <Control label="Dynamic response">
          <Select
            value={source.dynamicMode || "value"}
            onChange={(event) =>
              update({
                source: {
                  ...source,
                  dynamicMode: event.target.value as "value" | "choices",
                },
                ...(field.type === "checkbox" &&
                event.target.value === "choices"
                  ? { default: [] }
                  : {}),
              })
            }
          >
            <option value="value">Value</option>
            <option value="choices">Choice catalog</option>
          </Select>
          <p className="muted">
            The trusted host supplies current options and an optional default.
            Submitted values cannot add options. Images use managed asset IDs.
          </p>
        </Control>
      )}
      {source.kind === "dynamic" && (
        <Control label="Parameter form">
          <Select
            value={source.dynamicSourceId || ""}
            onChange={(event) =>
              update({
                source: {
                  ...source,
                  dynamicSourceId: event.target.value || undefined,
                },
              })
            }
          >
            <option value="">No public parameters</option>
            {(project.dynamicSources || []).map((group) => (
              <option key={group.id} value={group.id}>
                {group.name}
              </option>
            ))}
          </Select>
        </Control>
      )}
      {source.kind === "statement" && !dataset && (
        <Control label="Statement group dataset">
          <Select
            value=""
            onChange={(event) => {
              const data = latest.find(
                (item) => item.id === event.target.value,
              );
              if (data)
                update({ source: binding(data.id, data.version, "statement") });
            }}
          >
            <option value="">Choose a statement group</option>
            {latest
              .filter((item) => item.kind === "table")
              .map((item) => (
                <option key={item.id} value={item.id}>
                  {item.name} · v{item.version}
                </option>
              ))}
          </Select>
        </Control>
      )}
      {source.kind === "dam" ? (
        <>
          <Control label="Library provider">
            <input
              list={`dam-providers-${field.id}`}
              value={source.providerId || ""}
              onChange={(event) =>
                update({
                  source: { ...source, providerId: event.target.value },
                })
              }
            />
            <datalist id={`dam-providers-${field.id}`}>
              {providers.map((provider) => (
                <option key={provider.id} value={provider.id}>
                  {provider.label}
                </option>
              ))}
            </datalist>
          </Control>
          <Control label="Library root folder">
            <input
              value={source.folderId || ""}
              onChange={(event) =>
                update({ source: { ...source, folderId: event.target.value } })
              }
            />
          </Control>
          <p className="muted">
            Customers browse inside this root. Upload permission is configured
            separately in Image options.
          </p>
        </>
      ) : dataset ? (
        <>
          <Control label="Dataset version">
            <Select
              value={`${dataset.id}:${dataset.version}`}
              onChange={(e) => {
                const data = project.datasets.find(
                  (d) => `${d.id}:${d.version}` === e.target.value,
                )!;
                update({ source: binding(data.id, data.version) });
              }}
            >
              {project.datasets
                .filter(
                  (d) =>
                    !["lookup", "statement"].includes(source.kind) ||
                    d.kind === "table",
                )
                .map((d) => (
                  <option
                    key={`${d.id}:${d.version}`}
                    value={`${d.id}:${d.version}`}
                  >
                    {d.name} · v{d.version}
                  </option>
                ))}
            </Select>
          </Control>
          {source.kind === "statement" ? (
            <>
              <Control label="Statement label column">
                <Select
                  value={
                    source.labelColumn ||
                    dataset.labelColumn ||
                    dataset.primaryKey
                  }
                  onChange={(event) =>
                    update({
                      source: { ...source, labelColumn: event.target.value },
                    })
                  }
                >
                  {dataset.columns.map((column) => (
                    <option key={column.key} value={column.key}>
                      {column.label}
                    </option>
                  ))}
                </Select>
              </Control>
              <Control label="Statement content column">
                <Select
                  value={source.resultColumn || "content"}
                  onChange={(event) =>
                    update({
                      source: { ...source, resultColumn: event.target.value },
                    })
                  }
                >
                  {dataset.columns
                    .filter((column) => column.type === "string")
                    .map((column) => (
                      <option key={column.key} value={column.key}>
                        {column.label}
                      </option>
                    ))}
                </Select>
              </Control>
              <Control label="Default statement">
                <Select
                  value={source.statementId || ""}
                  onChange={(event) =>
                    update({
                      source: {
                        ...source,
                        statementId: event.target.value || undefined,
                      },
                    })
                  }
                >
                  <option value="">Any / literal default</option>
                  {statementOptions(project, field).map((option) => (
                    <option key={option.id} value={option.id}>
                      {option.label}
                    </option>
                  ))}
                </Select>
              </Control>
              <p className="muted">
                The selected version supplies reusable text. Customers can
                choose another statement or edit custom text when this variable
                is editable.
              </p>
            </>
          ) : (
            dataset.kind === "table" && (
              <>
                {source.kind === "lookup" && (
                  <Control label="Lookup selector">
                    <Select
                      value={source.lookupFieldId}
                      onChange={(e) =>
                        update({
                          source: { ...source, lookupFieldId: e.target.value },
                        })
                      }
                    >
                      {project.fields
                        .filter((f) => f.id !== field.id)
                        .map((f) => (
                          <option key={f.id} value={f.id}>
                            {f.label}
                          </option>
                        ))}
                    </Select>
                  </Control>
                )}
                {source.kind === "lookup" && (
                  <Control label="Lookup match operator">
                    <MatchOperator
                      label="Lookup match operator"
                      value={source.matchOperator}
                      onChange={(matchOperator) =>
                        update({ source: { ...source, matchOperator } })
                      }
                    />
                    <p className="muted">
                      Input is compared with each match-column value. The first
                      matching row supplies the result; no match uses the
                      default. Matching is case-sensitive. Regex patterns belong
                      in the match column.
                    </p>
                  </Control>
                )}
                {(source.kind === "lookup"
                  ? (["matchColumn", "resultColumn"] as const)
                  : (["valueColumn", "labelColumn"] as const)
                ).map((key) => (
                  <Control
                    key={key}
                    label={
                      key === "matchColumn"
                        ? "Match column"
                        : key === "resultColumn"
                          ? "Result column"
                          : key === "valueColumn"
                            ? "Value column"
                            : "Label column"
                    }
                  >
                    <Select
                      value={
                        source[key] ||
                        (key === "valueColumn"
                          ? dataset.valueColumn || dataset.primaryKey
                          : key === "labelColumn"
                            ? dataset.labelColumn || dataset.primaryKey
                            : dataset.primaryKey)
                      }
                      onChange={(e) =>
                        update({ source: { ...source, [key]: e.target.value } })
                      }
                    >
                      {dataset.columns.map((c) => (
                        <option key={c.key} value={c.key}>
                          {c.label}
                        </option>
                      ))}
                    </Select>
                  </Control>
                ))}
              </>
            )
          )}
          {source.kind === "lookup" && (
            <p className="muted">
              Derived from the selected row. Submitted values cannot override
              this variable.
            </p>
          )}
        </>
      ) : (
        source.kind !== "literal" && (
          <Control
            label={
              source.kind === "image-rule" ? "Image rule name" : "Lookup key"
            }
          >
            <input
              list={
                source.kind === "user"
                  ? "proof-user-lookups"
                  : source.kind === "order"
                    ? "proof-order-lookups"
                    : undefined
              }
              value={source.key || ""}
              onChange={(e) =>
                update({ source: { ...source, key: e.target.value } })
              }
            />
            {source.kind === "user" && (
              <datalist id="proof-user-lookups">
                {userLookupCatalog.flatMap((item) => [
                  <option key={item.key} value={item.key}>
                    {item.label} · {item.id}
                  </option>,
                  <option key={item.id} value={`lookup_${item.id}`}>
                    {item.label} · legacy ID {item.id}
                  </option>,
                ])}
              </datalist>
            )}
            {source.kind === "order" && (
              <datalist id="proof-order-lookups">
                {orderLookupCatalog.map((item) => (
                  <option key={item.key} value={item.key}>
                    {item.label} · {item.id}
                  </option>
                ))}
              </datalist>
            )}
          </Control>
        )
      )}
    </>
  );
}
